import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import { updateLocation, _getLocations } from '@/@core/queries/locations';
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { hasPermission } from '@/composables/usePermission'

export default function useLocationList() {
  const { showErrorMessage } = useNotifications();
  const { handleError } = useCommon()
  const { updateItem, updateItems } = useLambda()
  const { locationsQueryWithOffset } = useMongoDBFunctions()
  const refLocationListTable = ref(null)
  const locations = ref([])
  const userData = store.state?.userStore?.userData
  const role = userData?.role
  const zoneAgencyFilter = localStorage.getItem("clientData")
      ? JSON.parse(localStorage.getItem("clientData")).zone_agency_filter
      : false

  // Table Handlers
  const tableColumns = computed(() => {
    let headers = [
      {
        key: 'Actions',
        label: i18n.t('message.tableHeader.actions'),
      },
      {
        key: 'location',
        label: i18n.t('message.tableHeader.location'),
        sortable: true,
      },
    ]
    if (zoneAgencyFilter) {
      headers.splice(2, 0,
        {
          key: 'zone',
          label: i18n.t('message.tableHeader.zone'),
          sortable: true,
        },
        {
          key: 'agency',
          label: i18n.t('message.tableHeader.agency'),
          sortable: true,
        }
      )
    }
    return headers
  })

  const perPage = ref(10)
  const totalLocations = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('location')
  const isSortDirDesc = ref(false)
  const statusFilter = ref(null)
  const isLoading = ref(true)

  const refetchData = () => {
    if (refLocationListTable.value) {
      refLocationListTable.value.refresh()
    }
  }

  const fetchLocations = async () => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        query: {},
        search: searchQuery.value,
        sortBy: sortBy.value,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        client_id: ( role != "admin" ) ? userData.client.$oid : null
      }

      const { locations: l, totalRecords: total } = await locationsQueryWithOffset(input)

      locations.value = l
      if (currentPage.value === 1) totalLocations.value = total
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.location_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteLocation = async (userId, callback) => {
    if (!hasPermission('organization-locations', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      callback(false)
      return
    }

    try {
      const query = { _id: oid(userId), client_id: oid(userData.client.$oid) }
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection: 'location', query, action })

      callback(true)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
      callback(false)
    }
  }


  watch([currentPage, perPage, statusFilter], () => {
    fetchLocations()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchLocations()
  })

  return {
    fetchLocations,
    tableColumns,
    perPage,
    currentPage,
    totalLocations,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refLocationListTable,
    locations,
    statusFilter,
    refetchData,
    deleteLocation,
    isLoading,
    role,
  }
}